import { useRef, useState } from 'react';

import observeRect from '~shared/utils/observe-rect';

import { useIsomorphicLayoutEffect } from './';

export function useRect<T extends Element = HTMLElement>(
  nodeRef: React.RefObject<T | undefined | null>,
  observe = true,
  onChange?: (rect: DOMRect) => void
): null | DOMRect {
  const [element, setElement] = useState(nodeRef.current);
  const initialRectIsSet = useRef(false);
  const initialRefIsSet = useRef(false);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const onChangeRef = useRef<typeof onChange>();

  useIsomorphicLayoutEffect(() => {
    onChangeRef.current = onChange;

    if (nodeRef.current !== element) {
      setElement(nodeRef.current);
    }
  });

  useIsomorphicLayoutEffect(() => {
    if (element && !initialRectIsSet.current) {
      initialRectIsSet.current = true;
      setRect(element.getBoundingClientRect());
    }
  }, [element]);

  useIsomorphicLayoutEffect(() => {
    let observer: ReturnType<typeof observeRect> = undefined;
    let elem = element;

    function cleanup() {
      observer && observer.unobserve();
    }

    // State initializes before refs are placed, meaning the element state will
    // be undefined on the first render. We still want the rect on the first
    // render, so initially we'll use the nodeRef that was passed instead of
    // state for our measurements.
    if (!initialRefIsSet.current) {
      initialRefIsSet.current = true;
      elem = nodeRef.current;
    }

    if (!elem) {
      // if (__DEV__) {
      //   console.warn("You need to place the ref");
      // }
      return cleanup;
    }

    observer = observeRect(elem, (rect) => {
      onChangeRef.current && onChangeRef.current(rect);
      setRect(rect);
    });

    observe && observer.observe();

    return cleanup;
  }, [observe, element]);

  return rect;
}
